/*
* MAINTENANCE ADVICE:
* This file is a MOCK version of ReceiptsPage.jsx. 
* Please keep this file in sync with ReceiptsPage.jsx. 
* Any UI changes, styling updates, or logic improvements made to the live page 
* should be reflected here to ensure the mock experience remains accurate.
*/

import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Ensure cookies are sent
axios.defaults.withCredentials = true;
import { CATEGORY_OPTIONS } from './constants';

const MockReceiptsPage = () => {
    const [receipts, setReceipts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imageUrls, setImageUrls] = useState({}); // id -> blobURL
    const [loadingImages, setLoadingImages] = useState({}); // id -> bool
    const [selectedImage, setSelectedImage] = useState(null); // For lightbox
    const [isZoomed, setIsZoomed] = useState(false);
    const [activeFilter, setActiveFilter] = useState('all'); // 'all', 'pending', 'approved'

    // Parse URL query parameter for single-receipt filtering
    const filterId = new URLSearchParams(window.location.search).get('id');

    useEffect(() => {
        fetchReceipts();
    }, []);

    // Handle History/Back Button for Lightbox
    useEffect(() => {
        if (selectedImage) {
            window.history.pushState({ lightbox: true }, '');
            const handlePopState = () => {
                setSelectedImage(null);
                setIsZoomed(false);
            };
            window.addEventListener('popstate', handlePopState);
            return () => window.removeEventListener('popstate', handlePopState);
        }
    }, [selectedImage]);

    const fetchReceipts = async () => {
        // MOCK DATA
        // Simulating API delay
        setTimeout(() => {
            let mockData = [
                {
                    id: 101,
                    receipt_index: 0,
                    created_at: new Date().toISOString(),
                    date: '2025-11-26',
                    merchant_name: 'Poundland Limited',
                    category: 'office',
                    total: 7.00,
                    currency: 'GBP',
                    needs_review: false,
                    source: 'email'
                },
                {
                    id: 102,
                    receipt_index: 0,
                    created_at: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
                    date: null, // Simulate missing date
                    merchant_name: 'Tesco Express',
                    total: 10.50,
                    currency: 'GBP',
                    needs_review: true,
                    source: 'whatsapp'
                },
                {
                    id: 103,
                    receipt_index: 0,
                    created_at: new Date(Date.now() - 172800000).toISOString(), // 2 days ago
                    date: '2025-11-24',
                    merchant_name: 'Uber',
                    total: 18.25,
                    currency: 'GBP',
                    needs_review: false,
                    source: 'app'
                }
            ];

            // If filterId exists, return only that receipt (mocking backend filter)
            if (filterId) {
                mockData = mockData.filter(r => String(r.id) === filterId);
            }

            setReceipts(mockData);
            setLoading(false);
        }, 600);
    };

    // Auto-fetch images primarily for thumbnails
    useEffect(() => {
        receipts.forEach(receipt => {
            if (!imageUrls[receipt.id] && !loadingImages[receipt.id]) {
                fetchImage(receipt);
            }
        });
    }, [receipts]);

    const fetchImage = async (receipt) => {
        setLoadingImages(prev => ({ ...prev, [receipt.id]: true }));
        try {
            // Use local mock image
            const res = await fetch('/mock_receipt.jpg');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            setImageUrls(prev => ({ ...prev, [receipt.id]: url }));
        } catch (err) {
            console.error("Failed to fetch image for", receipt.id, err);
        } finally {
            setLoadingImages(prev => ({ ...prev, [receipt.id]: false }));
        }
    };

    // Filter receipts based on active filter tab
    const getFilteredReceipts = () => {
        if (filterId) return receipts; // If viewing single receipt, show it regardless of filter
        switch (activeFilter) {
            case 'pending':
                return receipts.filter(r => r.needs_review);
            case 'approved':
                return receipts.filter(r => !r.needs_review);
            default:
                return receipts;
        }
    };

    // Group receipts by status for "all" view
    const pendingReceipts = receipts.filter(r => r.needs_review);
    const approvedReceipts = receipts.filter(r => !r.needs_review);

    if (loading) return (
        <div className="min-h-screen bg-slate-950 flex items-center justify-center">
            <div className="text-indigo-500 animate-pulse">Loading mock receipts...</div>
        </div>
    );

    const filteredReceipts = getFilteredReceipts();

    const checkDelete = (receipt, e) => {
        e.stopPropagation();
        if (window.confirm('Delete this receipt? (Mock Action)')) {
            setReceipts(prev => prev.filter(r => r.id !== receipt.id));
        }
    };

    return (
        <div className="max-w-5xl mx-auto p-6 md:py-12">
            <header className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <h1 className="text-3xl font-extrabold tracking-tight text-white">
                            {filterId ? 'Receipt Details' : 'My Receipts'}
                        </h1>
                        <span className="bg-emerald-500/20 text-emerald-300 text-xs px-2 py-0.5 rounded border border-emerald-500/30 font-mono tracking-wider">MOCK</span>
                    </div>
                    <p className="text-slate-400">
                        {filterId
                            ? `Viewing receipt ID: ${filterId}`
                            : 'Digital archive of your submitted expenses (DEMO).'}
                    </p>
                </div>

                {/* Filter Tabs (only show when not viewing single receipt) */}
                {!filterId && (
                    <div className="flex bg-slate-900/50 backdrop-blur-md border border-white/5 rounded-full px-1 p-1">
                        <button
                            onClick={() => setActiveFilter('all')}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${activeFilter === 'all'
                                ? 'bg-indigo-500 text-white shadow-lg shadow-indigo-500/25'
                                : 'text-slate-400 hover:text-white'
                                }`}
                        >
                            All ({receipts.length})
                        </button>
                        <button
                            onClick={() => setActiveFilter('pending')}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${activeFilter === 'pending'
                                ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/25'
                                : 'text-slate-400 hover:text-white'
                                }`}
                        >
                            Pending ({pendingReceipts.length})
                        </button>
                        <button
                            onClick={() => setActiveFilter('approved')}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${activeFilter === 'approved'
                                ? 'bg-green-500 text-white shadow-lg shadow-green-500/25'
                                : 'text-slate-400 hover:text-white'
                                }`}
                        >
                            Approved ({approvedReceipts.length})
                        </button>
                    </div>
                )}
            </header>

            {/* Back to all receipts link when viewing single */}
            {filterId && (
                <div className="mb-6">
                    <a
                        href="/mockreceipts"
                        className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 transition-colors text-sm font-medium"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
                        </svg>
                        Back to all receipts
                    </a>
                </div>
            )}

            {error ? (
                <div className="bg-red-500/10 border border-red-500/20 text-red-200 p-6 rounded-xl text-center">
                    {error}
                </div>
            ) : filteredReceipts.length === 0 ? (
                <div className="bg-indigo-500/10 border border-indigo-500/20 text-indigo-200 p-12 rounded-2xl text-center">
                    <div className="text-4xl mb-4">📭</div>
                    <h3 className="text-xl font-bold mb-2">
                        {activeFilter === 'pending' ? 'No pending receipts' :
                            activeFilter === 'approved' ? 'No approved receipts' :
                                'No receipts yet'}
                    </h3>
                    <p className="text-indigo-300/70">
                        {activeFilter === 'pending'
                            ? 'All your receipts have been reviewed!'
                            : activeFilter === 'approved'
                                ? 'Process some receipts to see them here.'
                                : "You haven't submitted any receipts."}
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {filteredReceipts.map((receipt, index) => (
                        <div key={`${receipt.id}-${receipt.receipt_index || index}`} className={`bg-slate-900 border rounded-xl p-4 md:p-6 flex flex-col md:flex-row gap-6 items-start md:items-center transition-all hover:bg-slate-800/50 ${receipt.needs_review ? 'border-amber-500/30' : 'border-slate-800'
                            }`}>

                            {/* Image Thumbnail */}
                            <div className="w-full md:w-24 h-24 shrink-0 bg-black/50 rounded-lg overflow-hidden border border-white/5 relative group cursor-pointer"
                                onClick={() => imageUrls[receipt.id] && setSelectedImage(receipt)}>
                                {loadingImages[receipt.id] ? (
                                    <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-600">Loading...</div>
                                ) : imageUrls[receipt.id] ? (
                                    <>
                                        <img src={imageUrls[receipt.id]} alt="Thumbnail" className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" />
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="white" className="w-6 h-6">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
                                            </svg>
                                        </div>
                                    </>
                                ) : (
                                    <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-700">No Image</div>
                                )}
                            </div>

                            {/* Details */}
                            <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-5 gap-4 w-full">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Date</label>
                                    <div className="text-white font-medium">{new Date(receipt.date || receipt.values?.date || receipt.created_at).toLocaleDateString()}</div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Merchant</label>
                                    <div className="text-white font-medium truncate">{receipt.merchant_name || receipt.values?.merchant_name || <span className="text-slate-600 italic">Unknown</span>}</div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Category</label>
                                    <div className="text-white font-medium truncate">
                                        {(() => {
                                            const catValue = receipt.category || receipt.values?.category;
                                            const found = CATEGORY_OPTIONS.find(c => c.value === catValue);
                                            return found ? found.label : <span className="text-slate-500 italic">—</span>;
                                        })()}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Total</label>
                                    <div className="text-white font-medium">{(receipt.currency || receipt.values?.currency) === 'GBP' ? '£' : (receipt.currency || receipt.values?.currency || '')}{parseFloat(receipt.total || receipt.values?.total || 0).toFixed(2)}</div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Status</label>
                                    <div className="flex items-center gap-3">
                                        {receipt.needs_review ? (
                                            <a href={`/mockreview?id=${receipt.id}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-500/10 text-amber-500 rounded text-xs font-bold border border-amber-500/20 hover:bg-amber-500/20 transition-colors">
                                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                                                Fix Now
                                            </a>
                                        ) : (
                                            <>
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-500/10 text-green-500 rounded text-xs font-bold border border-green-500/20">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-3 h-3">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                                                    </svg>
                                                    Approved
                                                </span>
                                                <a href={`/mockreview?id=${receipt.id}&type=receipt`} className="text-slate-400 hover:text-indigo-400 transition-colors" title="Edit Receipt">
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                                                    </svg>
                                                </a>
                                            </>
                                        )}

                                        <button
                                            onClick={(e) => checkDelete(receipt, e)}
                                            className="text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                            title="Delete Receipt"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Lightbox Modal (Reused Logic) */}
            {selectedImage && (
                <div
                    className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center animate-in fade-in duration-200"
                    onClick={() => window.history.back()}
                >
                    <button
                        className="fixed top-4 right-4 bg-black/50 text-white p-2 z-[60] rounded-full hover:bg-black/70 backdrop-blur-md transition-all"
                        onClick={(e) => {
                            e.stopPropagation();
                            window.history.back();
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>

                    <div className={`w-full h-full overflow-auto p-4 ${isZoomed ? '' : 'flex items-center justify-center'}`}>
                        <img
                            src={imageUrls[selectedImage.id]}
                            alt="Zoomed Receipt"
                            className={`rounded shadow-2xl transition-all duration-300 ${isZoomed
                                ? 'w-full max-w-none cursor-zoom-out'
                                : 'max-h-full max-w-full object-contain cursor-zoom-in'
                                }`}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsZoomed(!isZoomed);
                            }}
                        />
                    </div>

                    <div className="fixed bottom-6 left-0 right-0 text-center pointer-events-none z-50">
                        <span className="bg-black/70 text-white/90 text-sm px-4 py-2 rounded-full backdrop-blur-md border border-white/10 shadow-lg">
                            Captured on {new Date(selectedImage.created_at).toLocaleDateString()} by {selectedImage.source}
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MockReceiptsPage;
